import { TIndicatedComissionLevelEnum } from './IndicatedTenant'

export enum TTenantAnticipationType {
  None = 0,
  D2 = 2,
  D7 = 7,
  D15 = 15,
  D30 = 30,
}

export enum TPersonType {
  Individual = 1,
  Corporation = 2,
}

export type ITenant = {
  id: string
  accessKey: string
  name: string
  email: string
  status?: number
  paymentServiceUrl?: string
  memberServiceDomain?: string
  memberServiceToken?: string
  memberServiceApiKey?: string
  token: string
  googleTagManagerId?: string
  createdDate?: Date
  updatedDate?: Date
  phone?: string
  enableMemberClass: boolean
  urlClub?: string | null | undefined
  enableRegisterProducts?: boolean | null | undefined
  enableCeletusClub?: boolean | null
  clubProducerFriendlyId?: string | null
}

export type ITenantBankAccount = {
  recipientDocument: string
  accountName: string
  bankCode: string
  agencyNumber: string
  accountNumber: string
  accountNumberDigit: string
  accountType: number
  agencyNumberCheck?: number | null
  tenantId?: string
}

export enum EIdentityValidation {
  Unverified = 1,
  Waiting = 2,
  Verified = 3,
  Pending = 4,
  Denied = 5,
}

export type ITenantBalance = {
  availableAmount: number
  waitingFundsAmount: number
  totalAmount: number
  transferredAmount: number
  withdraws: IWithdraw[]
  recipientStatus?: string
  immediateWithdrawal: number
  anticipationType?: TTenantAnticipationType | null
  minWithdrawValue: number
  isRequestIdentityValidation: boolean
  identityValidation: EIdentityValidation
  identityValidationDescription: string
  identityValidationUrl: string
  indicatedAmount?: number
  commissionsReceived?: number
  isSalesTeam: boolean
  withdrawAmountReleased?: number
  hasNegativeBalance?: boolean
  balanceWarnings?: string[]
  warrantyAmount?: number
  retainedAmount?: number
  withdrawLimitEnabled: boolean
  withdrawLimit: number | null
  withdrawLimitRemaining: number | null
}

export type TBalanceCurrency = 'brl' | 'usd'

export type ITenantUsdBalance = {
  availableAmount: number
  waitingFundsAmount: number
  totalAmount: number
  transferredAmount: number
  minWithdrawValue: number
  withdrawalsEnabled: boolean
  hasNegativeBalance?: boolean
  balanceWarnings?: string[]
  withdraws: IWithdraw[]
}

export type TIndentityVerificationURL = {
  identityValidationUrl: string
}

export type IWithdraw = {
  amount: number
  createdAt: Date
  status: WithdrawStatusEnum
  updatedAt?: Date
  details?: IWithdrawDetail[]
}

export const getWithdrawFee = (withdraw: IWithdraw): number => {
  return new Date(withdraw.createdAt).getFullYear() > 2025 ? 1.49 : 3.67
}

export const getWithdrawFeeByCurrency = (
  withdraw: IWithdraw,
  currency: TBalanceCurrency
): number => {
  return currency === 'usd' ? 0 : getWithdrawFee(withdraw)
}

export type IWithdrawDetail = {
  detail: string
  registerDate?: Date
}

export enum WithdrawStatusEnum {
  pending = 1,
  approved = 2,
  processing = 3,
  fail = 4,
}

export type TSignUpTenant = {
  name: string
  email: string
  emailConfirm?: string
  password: string
  phone: string
  indicatedOf?: string | null
}

export type TTenantSettings = {
  id: string
  tenantId: string
  immediateWithdrawal: number

  tenantCode?: string
  tenantName?: string
  tenantEmail?: string
  tenantPhone?: string
  recipientId?: string

  enableAnticipationD15?: boolean | null
  anticipationD15Fee?: number | null
  enableAnticipationD7?: boolean | null
  anticipationD7Fee?: number | null
  enableAnticipationD2?: boolean | null
  anticipationD2Fee?: number | null
  chargeAnticipationOnPix?: boolean | null

  disableIndicatedComissions?: boolean | null
  indicatedComissionLevel?: TIndicatedComissionLevelEnum | null
  indicatedComissionsRateValue?: number | null
  acceptIndicationUseTerms?: boolean | null

  recipientStatus?: string | null

  withdrawlsEnabled?: boolean | null
  withdrawLimitEnabled?: boolean | null
  withdrawLimit?: number | null
  salesEnabled?: boolean | null
  enableRegisterProducts?: boolean | null
  requestValidationForProducts?: boolean | null

  enableAffiliateFees?: boolean | null
  affiliateFees?: number | null
  anticipationType?: TTenantAnticipationType | null

  permissionCodeAdmin?: string | null

  capturePixFee?: number | null
  captureCardFee?: number | null
  captureBoletoFee?: number | null
  mdrPixFee?: number | null
  mdrCardFee?: number | null
  mdrBoletoFee?: number | null
  enableCeletusClub?: boolean | null
  clubProducerFriendlyId?: string | null

  // Segurança
  enablePagarme3DS?: boolean | null
  enableDeviceFingerprint?: boolean | null
  enableVelocityCheck?: boolean | null
  enableGooglePay?: boolean | null
  enableApplePay?: boolean | null
  enableChargebackDispute?: boolean | null
  enableRefundSelfCancel?: boolean | null
  maxCardAttemptsPerHour?: number | null
  cardAttemptsWindowInMinutes?: number | null
  maxDocumentTransactionsPerDay?: number | null
  documentTransactionsWindowInHours?: number | null
  consecutiveCardDeclinesToBlock?: number | null
  cardTemporaryBlockMinutes?: number | null
  dailyCardAmountLimit?: number | null
  internationalSalesEnabled?: boolean | null
}

export type TTenantSettingsPagination = {
  items: TTenantSettings[]
  page: number
  rowsPerPage: number
  totalPages: number
  total: number
}

export type TTenantAddressRegister = {
  street: string
  complement: string
  number: string
  neighborhood: string
  city: string
  state: string
  zipCode: string
  referencePoint: string
}

export type TTenantManagingPartner = {
  name: string
  email: string
  document: string
  birthDate: Date | null
  monthyIncome: number
  declaredLegal: boolean
  phone: string
  professionalOccupation: string
  address: TTenantAddressRegister
}

export type TTenantRegister = {
  id?: string
  personType: TPersonType
  email: string
  document: string
  phone: string
  address: TTenantAddressRegister
  bankAccount?: ITenantBankAccount | null

  name: string
  revenue: number

  birthDate?: Date | null
  professionalOccupation?: string | null

  tradingName?: string | null
  companyFoundationDate?: Date | null
  mainAddress?: TTenantAddressRegister
  managingPartner?: TTenantManagingPartner
}

export type TSalesAnalysisByProducer = {
  id: string
  name: string
  email: string

  allTransactionsLast30Days?: number
  allTransactionsLastGeneral?: number

  allCommissionsReceived?: number
  allCommissionsReceivedLast30Days?: number

  allWithdraws?: number
  allAccountBalance?: number
  allBalance?: number

  celetusGeneralIncame?: number
  celetusLast30DaysIncame?: number
  allGatewayFees?: number
  allGatewayFeesLast30Days?: number
  allProfit?: number
  allProfitLast30Days?: number
  allTransactionsLast30DaysUsd?: number
  allTransactionsLastGeneralUsd?: number
  allCommissionsReceivedUsd?: number
  allCommissionsReceivedLast30DaysUsd?: number
  allProfitUsd?: number
  allProfitLast30DaysUsd?: number
  celetusGeneralIncameUsd?: number
  celetusLast30DaysIncameUsd?: number

  createdDate: Date
  updatedDate: Date
}

export type TSalesAnalysisByProducerPagination = {
  items: TSalesAnalysisByProducer[]
  page: number
  rowsPerPage: number
  totalPages: number
  total: number
  info?: number
}

export type TTenantDivergentBalance = {
  id: string
  tenantId: string
  tenantCode?: string
  tenantName?: string
  tenantEmail?: string
  tenantPhone?: string
  corretValue: number
  incorretValue: number
  differenceValue: number
}

export type TTenantDivergentBalancePagination = {
  items: TTenantDivergentBalance[]
  page: number
  rowsPerPage: number
  totalPages: number
  total: number
}

export type TTenantAssuranceValue = {
  producName: string
  name: string
  value: number
  createdDate: string
}
